import {
  TextField,
  Button,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Chip,
  TableContainer,
  Checkbox,
  InputAdornment,
} from '@mui/material'

import { useEffect, useState } from 'react'
import {
  linkZendeskTickets,
  searchZendeskTickets,
} from '../../Apis/routes/zendesk'
import { useDebounce } from '../../hooks/useDebounce'
import moment from 'moment'
import { getStatusDisplayInfo } from '../utils/zendesk.utils'
import { CircularProgress } from '@material-ui/core'
import LinkIcon from '@mui/icons-material/Link'

export const ZendeskTicketSearchTable = ({
  ticketId,
  zendeskTicketsAlreadyLinked,
  navigateToLinkedView,
  type,
}) => {
  const [zendeskTickets, setZendeskTickets] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [ticketsSelected, setTicketsSelected] = useState([])
  const debouncedSearchQuery = useDebounce(searchQuery, 1000)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (debouncedSearchQuery) setIsLoading(true)
    searchZendeskTickets(debouncedSearchQuery).then((zendeskTicketsFetched) => {
      setIsLoading(false)
      if (!zendeskTicketsFetched?.length) {
        return
      }
      const zendeskTicketsFiltered = zendeskTicketsFetched.filter(
        (zendeskTicket) =>
          !zendeskTicketsAlreadyLinked.find(
            (zendeskTicketAlreadyLinked) =>
              zendeskTicketAlreadyLinked.zendeskId === zendeskTicket.zendeskId
          )
      )
      setZendeskTickets(zendeskTicketsFiltered)
      setTicketsSelected([])
    })
  }, [debouncedSearchQuery])

  const handleClickRow = (ticketSelected) => {
    setTicketsSelected((prevState) => {
      const isAlreadySelected = prevState.find(
        (ticket) => ticket.zendeskId === ticketSelected.zendeskId
      )
      if (!isAlreadySelected) {
        return [...prevState, ticketSelected]
      } else {
        return prevState.filter(
          (ticket) => ticket.zendeskId !== ticketSelected.zendeskId
        )
      }
    })
  }

  const handleClickLinkTickets = async () => {
    await linkZendeskTickets(ticketId, ticketsSelected, type)
    navigateToLinkedView()
  }

  const isSelected = (row) =>
    ticketsSelected.findIndex(
      (ticket) => ticket.zendeskId === row.zendeskId
    ) !== -1

  return (
    <>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Demandeur, assigné, email, sujet, id, etc."
        value={searchQuery}
        label="Rechercher"
        sx={{ my: 2 }}
        onChange={(e) => {
          setSearchQuery(e.target.value)
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isLoading && <CircularProgress color="inherit" size={20} />}
            </InputAdornment>
          ),
        }}
      />
      {zendeskTickets.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="left">ID</TableCell>
                <TableCell align="left">Status</TableCell>
                <TableCell align="left">Canal</TableCell>
                <TableCell align="left">Demandeur</TableCell>
                <TableCell align="left">Sujet</TableCell>
                <TableCell align="left">Assigné</TableCell>
                <TableCell align="left">Crée le</TableCell>
                <TableCell align="left">Mise à jour</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zendeskTickets.map((zendeskTicket) => {
                const isTicketSelected = isSelected(zendeskTicket)
                const { colorStatus, translatedStatus } = getStatusDisplayInfo(
                  zendeskTicket.status
                )

                return (
                  <TableRow
                    hover
                    onClick={() => handleClickRow(zendeskTicket)}
                    role="checkbox"
                    aria-checked={isTicketSelected}
                    tabIndex={-1}
                    key={zendeskTicket.zendeskId}
                    selected={isTicketSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox color="primary" checked={isTicketSelected} />
                    </TableCell>
                    <TableCell component="th" scope="row" align="left">
                      <Link
                        href={zendeskTicket.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {zendeskTicket.zendeskId}
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={translatedStatus}
                        color={colorStatus}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="left">{zendeskTicket.channel}</TableCell>
                    <TableCell align="left">
                      {zendeskTicket.requester}
                    </TableCell>
                    <TableCell align="left">{zendeskTicket.subject}</TableCell>
                    <TableCell align="left">{zendeskTicket.assignee}</TableCell>
                    <TableCell align="left">
                      {moment(zendeskTicket.createdAt).format('DD/MM/YYYY')}
                    </TableCell>
                    <TableCell>
                      {moment(zendeskTicket.updatedAt).format('DD/MM/YYYY')}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          paddingY: 2,
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleClickLinkTickets}
            disabled={ticketsSelected.length === 0}
            size="large"
            startIcon={<LinkIcon />}
          >
            Lier {ticketsSelected.length}
            {ticketsSelected.length > 1 ? ' tickets' : ' ticket'} Zendesk
          </Button>
        </Box>
      </Box>
    </>
  )
}
